import { useEstabsContext } from '../../contexts/EstabsContext';
import { zonedDate } from '../../utils/date-time';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PriceInput from '../../components/inputs/Price';
import { useAppContext } from '../../contexts/AppContext';
import { useParamsContext } from '../../contexts/ParamsContext';
import { formatBRL } from '../agenda/cliente/List/helpers';
import { SettingsRow, SettingsSection } from '../configuracoes/estabelecimento/Form/SettingsGroup';
import { UserService } from '../configuracoes/usuario/Service';
import { EstoqueService } from '../estoque/Service';
import { formatQuantity } from '../estoque/units';
import { normalizeSearch } from '../recorrencias/helpers';
import ClientSelect, { type ClientOption } from './ClientSelect';
import { useVendasContext } from './Context';
import {
    calculateTotals,
    getApiErrorMessage,
    getDiscountRuleLabel,
    PAYMENT_METHOD_LABELS,
    readLastPaymentMethod,
    saveLastPaymentMethod,
} from './helpers';
import { useDefaultPaymentMethod } from '../financeiro/useDefaultPaymentMethod';
import { usePlanAccess } from '../../plans/usePlanAccess';
import { SALONFLOW_FEATURES } from '../../plans/salonflowPlans';
import { SaleTotals } from './SaleParts';
import { VendasService } from './Service';
import { Minus, PackageOpen, Plus, Search, TriangleAlert, X } from 'lucide-react';

const estoqueService = new EstoqueService();
const userService = new UserService();
const vendasService = new VendasService();

const PICKER_LIMIT = 8;
const WHOLE_UNITS = ['UN', 'PCT', 'CX'];

type SaleLine = {
    itemId: string;
    quantity: number | string;
    unitPrice: number | '';
};

const getOnHand = (item: any) => Number(item?.balance?.onHand ?? 0);

const PAYMENT_PENDING = 'PENDING';

export default function SaleForm({ recurring = false }: { recurring?: boolean }) {
    const { estabSession } = useEstabsContext();
    const navigate = useNavigate();
    const { clear, setParams } = useParamsContext();
    const { successSnackbar } = useAppContext();
    const { reloadVendas, reloadRecorrentes } = useVendasContext();

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const [items, setItems] = useState<any[]>([]);
    const [clients, setClients] = useState<ClientOption[]>([]);
    const [recurringOptions, setRecurringOptions] = useState<any[]>([]);

    const [clientId, setClientId] = useState('');
    const [recorrenteId, setRecorrenteId] = useState('');
    const [lines, setLines] = useState<SaleLine[]>([]);
    const [search, setSearch] = useState('');
    const [discountType, setDiscountType] = useState('');
    const [discountValue, setDiscountValue] = useState<number | ''>('');
    const [payment, setPayment] = useState(readLastPaymentMethod);

    const { hasFeature } = usePlanAccess();
    const asksPayment = !recurring && hasFeature(SALONFLOW_FEATURES.FINANCE);
    const defaultMethod = useDefaultPaymentMethod();

    useEffect(() => {
        if (defaultMethod) setPayment(defaultMethod);
    }, [defaultMethod]);

    const loadItems = useCallback(async () => {
        const response = await estoqueService.findItems({ type: 'REVENDA', isActive: true, page: 1, limit: 500 });
        setItems(response?.data?.data || []);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const [clientsResponse] = await Promise.all([userService.getClients(), loadItems()]);
            setClients(
                (clientsResponse?.data || []).map((client: any) => ({
                    value: client.uid,
                    label: client.displayName || 'Cliente',
                }))
            );
        } catch (e) {
            console.error('Erro ao carregar dados da venda', e);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, [loadItems]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setRecorrenteId('');
        setRecurringOptions([]);
        if (recurring || !clientId) return;

        let active = true;
        vendasService
            .findRecorrentes({ page: 1, limit: 100, status: 'ATIVA', clientId })
            .then((response) => {
                if (active) setRecurringOptions(response?.data?.data || []);
            })
            .catch((e) => console.error('Erro ao carregar recorrências do cliente', e));

        return () => {
            active = false;
        };
    }, [clientId, recurring]);

    const itemById = useMemo(() => new Map(items.map((item) => [item._id, item])), [items]);

    const pickerResults = useMemo(() => {
        const term = normalizeSearch(search);
        const inCart = new Set(lines.map((line) => line.itemId));

        return items
            .filter((item) => !inCart.has(item._id))
            .filter((item) => !term || normalizeSearch(`${item.name} ${item.sku ?? ''}`).includes(term))
            .sort((a, b) => (recurring ? 0 : Number(getOnHand(b) > 0) - Number(getOnHand(a) > 0)))
            .slice(0, PICKER_LIMIT);
    }, [items, lines, recurring, search]);

    const subTotal = lines.reduce((sum, line) => sum + Number(line.quantity || 0) * Number(line.unitPrice || 0), 0);
    const totals = calculateTotals(subTotal, discountType, Number(discountValue || 0));

    const addItem = (item: any) => {
        setLines((prev) => [...prev, { itemId: item._id, quantity: 1, unitPrice: item.salePrice ?? '' }]);
        setSearch('');
        setError('');
    };

    const updateLine = (itemId: string, patch: Partial<SaleLine>) => {
        setLines((prev) => prev.map((line) => (line.itemId === itemId ? { ...line, ...patch } : line)));
    };

    const removeLine = (itemId: string) => {
        setLines((prev) => prev.filter((line) => line.itemId !== itemId));
    };

    const validate = () => {
        if (recurring && !clientId) return 'Selecione o cliente da recorrência.';
        if (!lines.length) return 'Adicione pelo menos um produto.';
        if (lines.some((line) => !(Number(line.quantity) > 0))) return 'Informe uma quantidade maior que zero em todos os produtos.';
        if (lines.some((line) => line.unitPrice === '')) return 'Informe o preço de todos os produtos.';
        if (discountType === 'PERCENTUAL' && Number(discountValue) > 100) return 'O desconto não pode passar de 100%.';
        if (discountType === 'VALOR' && Number(discountValue) > subTotal) return 'O desconto não pode ser maior que o subtotal.';
        return '';
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        const message = validate();
        if (message) {
            setError(message);
            return;
        }

        const hasDiscount = !!discountType && Number(discountValue) > 0;
        const payload = {
            clientId: clientId || undefined,
            items: lines.map((line) => ({
                itemId: line.itemId,
                quantity: Number(line.quantity),
                unitPrice: Number(line.unitPrice),
            })),
            discountType: hasDiscount ? discountType : undefined,
            discountValue: hasDiscount ? Number(discountValue) : undefined,
        };

        setSaving(true);
        setError('');
        try {
            if (recurring) {
                await vendasService.createRecorrente({ ...payload, frequency: 'SEMANAL', dayOfWeek: zonedDate(new Date(), estabSession?.timezone).day() });
                successSnackbar('Recorrência criada.');
                await reloadRecorrentes();
                setParams({ action: 'recorrentes' });
                return;
            }

            const isPending = payment === PAYMENT_PENDING;
            await vendasService.create({
                ...payload,
                recorrenteId: recorrenteId || undefined,
                ...(asksPayment
                    ? {
                          paymentStatus: isPending ? 'PENDING' : 'PAID',
                          paymentMethod: isPending ? undefined : payment,
                      }
                    : {}),
            });
            if (asksPayment && !isPending) saveLastPaymentMethod(payment);
            successSnackbar('Venda registrada. O estoque foi baixado.');
            await Promise.all([reloadVendas(), reloadRecorrentes()]);
            clear();
        } catch (e: any) {
            console.error(e);
            setError(getApiErrorMessage(e, recurring ? 'Não foi possível salvar a recorrência.' : 'Não foi possível concluir a venda.'));
            setSaving(false);
            // O saldo pode ter mudado desde a abertura do formulario.
            if (e?.response?.status === 409) void loadItems().catch(console.error);
        }
    };

    if (loading) {
        return (
            <div className="mx-auto grid w-full max-w-5xl gap-6 px-2 lg:grid-cols-[minmax(0,1fr)_20rem]" aria-busy="true">
                <div className="sf-skeleton h-64" />
                <div className="sf-skeleton h-40" />
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar produtos e clientes</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void load()}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!items.length) {
        return (
            <div className="sf-state">
                <PackageOpen aria-hidden />
                <h2>Nenhum produto de revenda</h2>
                <p>Cadastre no estoque um item ativo do tipo revenda para poder vender.</p>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/estoque?action=items')}>
                    Ir para o estoque
                </button>
            </div>
        );
    }

    return (
        <form
            className="estab-settings mx-auto grid w-full max-w-5xl gap-6 px-2 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start"
            onSubmit={handleSubmit}
        >
            <div className="min-w-0 space-y-6">
                <SettingsSection
                    title={recurring ? 'Cliente' : 'Cliente (opcional)'}
                    footer={recurring ? 'A recorrência fica vinculada a este cliente.' : undefined}
                >
                    <div className="p-3">
                        <ClientSelect
                            value={clientId}
                            onChange={setClientId}
                            clients={clients}
                            setClients={setClients}
                            label=""
                            onCreateError={setError}
                        />
                    </div>
                    {recurringOptions.length > 0 && (
                        <SettingsRow label="Recorrência" htmlFor="venda-recorrencia">
                            <select
                                id="venda-recorrencia"
                                className="select estab-field"
                                value={recorrenteId}
                                onChange={(e) => setRecorrenteId(e.target.value)}
                            >
                                <option value="">Sem vínculo</option>
                                {recurringOptions.map((option) => (
                                    <option key={option._id} value={option._id}>
                                        {formatBRL(option.recurringTotal)} · {(option.items || []).map((item: any) => item.name).join(', ')}
                                    </option>
                                ))}
                            </select>
                        </SettingsRow>
                    )}
                </SettingsSection>

                <SettingsSection
                    title="Produtos"
                    footer={recurring ? 'Os preços ficam fixos nesta recorrência, mesmo que o preço do produto mude depois.' : undefined}
                >
                    {lines.length > 0 && (
                        <ul className="divide-y divide-base-content/10">
                            {lines.map((line) => {
                                const item = itemById.get(line.itemId);
                                const quantity = Number(line.quantity || 0);
                                const onHand = getOnHand(item);
                                const overStock = !recurring && quantity > onHand;
                                const whole = WHOLE_UNITS.includes(item?.unit);

                                return (
                                    <li key={line.itemId} className="p-3 sm:px-4">
                                        <div className="flex items-start gap-2">
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-[0.9375rem] font-medium">{item?.name}</p>
                                                {!recurring && (
                                                    <p className={`text-xs ${overStock ? 'font-medium text-error' : 'text-base-content/55'}`}>
                                                        {overStock ? 'Saldo insuficiente: ' : 'Saldo: '}
                                                        {formatQuantity(onHand)} {item?.unit}
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm btn-square text-base-content/50"
                                                aria-label={`Remover ${item?.name}`}
                                                onClick={() => removeLine(line.itemId)}
                                            >
                                                <X aria-hidden />
                                            </button>
                                        </div>
                                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                                            <div className="flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-sm btn-square"
                                                    aria-label={`Diminuir ${item?.name}`}
                                                    disabled={quantity <= 1}
                                                    onClick={() => updateLine(line.itemId, { quantity: quantity - 1 })}
                                                >
                                                    <Minus aria-hidden />
                                                </button>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={whole ? 1 : 'any'}
                                                    inputMode={whole ? 'numeric' : 'decimal'}
                                                    className="input input-bordered input-sm w-16 text-center"
                                                    aria-label={`Quantidade de ${item?.name}`}
                                                    value={line.quantity}
                                                    onChange={(e) => updateLine(line.itemId, { quantity: e.target.value })}
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-sm btn-square"
                                                    aria-label={`Aumentar ${item?.name}`}
                                                    onClick={() => updateLine(line.itemId, { quantity: quantity + 1 })}
                                                >
                                                    <Plus aria-hidden />
                                                </button>
                                            </div>
                                            <div className="w-28">
                                                <PriceInput
                                                    label=""
                                                    ariaLabel={`Preço de ${item?.name}`}
                                                    placeholder="R$ 0,00"
                                                    className={`input-sm w-full text-right ${line.unitPrice === '' ? 'input-error' : ''}`}
                                                    value={line.unitPrice}
                                                    onChange={(e) => updateLine(line.itemId, { unitPrice: e.target.value as any })}
                                                />
                                            </div>
                                            <span className="ml-auto text-sm font-semibold tabular-nums">
                                                {formatBRL(quantity * Number(line.unitPrice || 0))}
                                            </span>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    <div className={`p-3 ${lines.length ? 'border-t border-base-content/10' : ''}`}>
                        <label className="sf-search">
                            <Search aria-hidden />
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Adicionar produto"
                                aria-label="Buscar produto por nome ou SKU"
                            />
                        </label>

                        {(!!search || !lines.length) &&
                            (pickerResults.length ? (
                                <ul className="sf-group mt-2">
                                    {pickerResults.map((item) => {
                                        const unavailable = !recurring && getOnHand(item) <= 0;

                                        return (
                                            <li key={item._id}>
                                                <button
                                                    type="button"
                                                    className={`sf-row ${unavailable ? 'cursor-not-allowed opacity-50' : ''}`}
                                                    disabled={unavailable}
                                                    onClick={() => addItem(item)}
                                                >
                                                    <span className="sf-row-body">
                                                        <span className="sf-row-title">{item.name}</span>
                                                        {!recurring && (
                                                            <span className="sf-row-sub">
                                                                {unavailable
                                                                    ? 'Sem saldo'
                                                                    : `Saldo: ${formatQuantity(getOnHand(item))} ${item.unit}`}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className="sf-row-end">{formatBRL(item.salePrice)}</span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <p className="px-1 pt-3 text-sm text-base-content/55">Nenhum produto encontrado.</p>
                            ))}
                    </div>
                </SettingsSection>
            </div>

            <div className="min-w-0 space-y-4 lg:sticky lg:top-4">
                <section>
                    <h2 className="sf-group-title">Desconto</h2>
                    <div className="sf-segmented" role="group" aria-label="Tipo de desconto">
                        {[
                            { value: '', label: 'Nenhum' },
                            { value: 'PERCENTUAL', label: '%' },
                            { value: 'VALOR', label: 'R$' },
                        ].map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                aria-pressed={discountType === option.value}
                                onClick={() => {
                                    setDiscountType(option.value);
                                    setDiscountValue('');
                                }}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    {discountType === 'PERCENTUAL' && (
                        <input
                            type="number"
                            min={0}
                            max={100}
                            step="any"
                            inputMode="decimal"
                            className="input input-bordered input-sm mt-2 w-full"
                            aria-label="Desconto em percentual"
                            placeholder="0%"
                            value={discountValue}
                            onChange={(e) => setDiscountValue(e.target.value === '' ? '' : Number(e.target.value))}
                        />
                    )}
                    {discountType === 'VALOR' && (
                        <div className="mt-2">
                            <PriceInput
                                label=""
                                ariaLabel="Desconto em reais"
                                placeholder="R$ 0,00"
                                className="input-sm w-full"
                                value={discountValue}
                                onChange={(e) => setDiscountValue(e.target.value as any)}
                            />
                        </div>
                    )}
                </section>

                {asksPayment && (
                    <section>
                        <h2 className="sf-group-title">
                            <label htmlFor="venda-pagamento">Pagamento</label>
                        </h2>
                        <select
                            id="venda-pagamento"
                            className="select select-bordered select-sm w-full"
                            value={payment}
                            onChange={(e) => setPayment(e.target.value)}
                        >
                            {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                            <option value={PAYMENT_PENDING}>A receber</option>
                        </select>
                    </section>
                )}

                <SaleTotals
                    subTotal={totals.subTotal}
                    discountAmount={totals.discountAmount}
                    discountRule={getDiscountRuleLabel(discountType, Number(discountValue))}
                    total={totals.total}
                />

                {!!error && (
                    <p className="px-1 text-sm text-error" role="alert">
                        {error}
                    </p>
                )}

                <div className="estab-savebar">
                    <button type="submit" className="btn btn-primary w-full" disabled={saving}>
                        {saving && <span className="loading loading-spinner loading-sm"></span>}
                        {recurring ? 'Salvar recorrência' : `Concluir venda · ${formatBRL(totals.total)}`}
                    </button>
                </div>
            </div>
        </form>
    );
}
